/** A route-cost scenario's corridors (neighborhood protection), as the pricing panel, its
 *  map and the workshop show them. Each corridor has the streets the route cost weighs
 *  (treated_edges), the same-class residential streets beside them when its spec lists
 *  them (parallel_edges, G) and groups the bake only reports, by label, whose added
 *  traffic is the treatment's aim. The pack keeps that shape twice: per corridor the
 *  streets' edge ids, the same at every hour; per hour each group's figures. */

// Violet, apart from the charging zone's amber and the V/C palette.
export const BAND_OUTLINE = [167, 139, 250];

/** A corridor's groups in the order they are shown: { key, kind, label, value }, where
 *  `value` is what `corridor` holds for the group (its edge ids or its figures). */
export function groupsOf(corridor) {
  const out = [{ key: 'treated', kind: 'treated', label: null, value: corridor.treated_edges }];
  if (corridor.parallel_edges) out.push({ key: 'parallel', kind: 'parallel', label: null, value: corridor.parallel_edges });
  for (const [label, value] of Object.entries(corridor.reported || {})) {
    out.push({ key: `reported:${label}`, kind: 'reported', label, value });
  }
  return out;
}

const factorText = (factor) => {
  if (Array.isArray(factor)) return `κ ${Math.min(...factor).toFixed(1)}–${Math.max(...factor).toFixed(1)}`;
  return factor ? `κ = ${Number(factor).toFixed(1)}` : null;
};

export function groupName(group, factor = null) {
  if (group.kind === 'treated') return `Band streets${factorText(factor) ? ` (${factorText(factor)})` : ''}`;
  if (group.kind === 'parallel') return 'Parallel streets (G)';
  return group.label;
}

/** The name a corridor is shown by, from its id ('rule-church' → 'Church band'). */
export const corridorName = (name) => `${name.replace(/^rule-/, '').replace(/^./, (x) => x.toUpperCase())} band`;

export const ratioText = (x) => (x === null || x === undefined ? '—' : x.toFixed(2));

/** A note as the pack gives it for an hour, or summed over hours: { text, hours }. */
export function noteText(note, hourLabel) {
  if (!note) return null;
  if (typeof note === 'string') return note;
  return `${note.hours.map(hourLabel).join(', ')}: ${note.text}`;
}

/** Every corridor's band streets (treated_edges), once each. */
export const treatedEdgeIds = (corridors) => [...new Set(Object.values(corridors).flatMap((c) => c.treated_edges))];

/** The area the bands protect, as a list of { ring } with closed rings: the union of each
 *  corridor's convex hull of its band streets, padded by `pad` degrees. One ring when the
 *  hulls share ground, else one per corridor. Not one hull over all the streets: crossing
 *  bands would take in the blocks between their arms, and part of the charging zone.
 *  `paths` maps an edge id to its [lng, lat] path. */
export function bandOutlines(corridors, paths, pad = 0.0004) {
  const hulls = [];
  for (const c of Object.values(corridors)) {
    const pts = [];
    for (const id of c.treated_edges) {
      for (const [x, y] of paths[id] || []) {
        pts.push([x - pad, y - pad], [x + pad, y - pad], [x + pad, y + pad], [x - pad, y + pad]);
      }
    }
    if (pts.length) hulls.push(convexHull(pts));
  }
  const union = hulls.length > 1 ? convexUnion(hulls) : null;
  return (union ? [union] : hulls).map((ring) => ({ ring }));
}

const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

/** Whether `p` lies strictly inside the counter-clockwise convex ring. */
function insideConvex(ring, p) {
  for (let i = 0; i < ring.length - 1; i++) {
    if (cross(ring[i], ring[i + 1], p) <= 1e-14) return false;
  }
  return true;
}

/** `subject` clipped to the counter-clockwise convex `clip` (Sutherland–Hodgman), open. */
function clipConvex(subject, clip) {
  let out = subject;
  for (let i = 0; i < clip.length - 1 && out.length; i++) {
    const [a, b] = [clip[i], clip[i + 1]];
    const input = out;
    out = [];
    for (let j = 0; j < input.length; j++) {
      const p = input[j];
      const q = input[(j + 1) % input.length];
      const [dp, dq] = [cross(a, b, p), cross(a, b, q)];
      if (dp >= 0) out.push(p);
      if ((dp >= 0) !== (dq >= 0)) {
        const t = dp / (dp - dq);
        out.push([p[0] + t * (q[0] - p[0]), p[1] + t * (q[1] - p[1])]);
      }
    }
  }
  return out;
}

/** Where segments ab and cd cross, or null. */
function segmentCrossing(a, b, c, d) {
  const den = (b[0] - a[0]) * (d[1] - c[1]) - (b[1] - a[1]) * (d[0] - c[0]);
  if (den === 0) return null;
  const t = ((c[0] - a[0]) * (d[1] - c[1]) - (c[1] - a[1]) * (d[0] - c[0])) / den;
  const u = ((c[0] - a[0]) * (b[1] - a[1]) - (c[1] - a[1]) * (b[0] - a[0])) / den;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1 ? [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])] : null;
}

/** The union of closed counter-clockwise convex rings that all share some ground, as one
 *  closed ring, or null when they don't. Every such union is star-shaped around any point
 *  they share, so its corners (each ring's corners outside the others, and the edges'
 *  crossings) taken in angle order around that point trace its boundary. */
function convexUnion(rings) {
  const common = rings.slice(1).reduce((acc, r) => clipConvex(acc, r), rings[0].slice(0, -1));
  if (common.length < 3) return null;
  const c = [common.reduce((s, p) => s + p[0], 0) / common.length, common.reduce((s, p) => s + p[1], 0) / common.length];
  const outsideOthers = (p, i, j = i) => rings.every((r, k) => k === i || k === j || !insideConvex(r, p));
  const corners = [];
  rings.forEach((r, i) => {
    for (let a = 0; a < r.length - 1; a++) {
      if (outsideOthers(r[a], i)) corners.push(r[a]);
      for (let j = i + 1; j < rings.length; j++) {
        const s = rings[j];
        for (let b = 0; b < s.length - 1; b++) {
          const x = segmentCrossing(r[a], r[a + 1], s[b], s[b + 1]);
          if (x && outsideOthers(x, i, j)) corners.push(x);
        }
      }
    }
  });
  corners.sort((p, q) => Math.atan2(p[1] - c[1], p[0] - c[0]) - Math.atan2(q[1] - c[1], q[0] - c[0]));
  return [...corners, corners[0]];
}

/** Andrew's monotone chain; the ring is counter-clockwise and closed. */
function convexHull(points) {
  const pts = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const half = (list) => {
    const h = [];
    for (const p of list) {
      while (h.length >= 2 && cross(h[h.length - 2], h[h.length - 1], p) <= 0) h.pop();
      h.push(p);
    }
    h.pop();
    return h;
  };
  const ring = [...half(pts), ...half([...pts].reverse())];
  return [...ring, ring[0]];
}
